// utils/crmWidgetUtils.js - OGESTYLE Helpdesk Widget Integration Utilities

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

/**
 * Decode JWT token payload
 * @param {string} token - JWT token
 * @returns {Object|null} - Decoded payload or null if invalid
 */
function decodeJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(base64);
    return JSON.parse(
      decodeURIComponent(
        [...json]
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      ),
    );
  } catch {
    return null;
  }
}

/**
 * Get role priority for primaryRole determination
 * Higher number = higher priority
 */
const ROLE_PRIORITY = {
  R6: 7, // Portal Admin (excluded from CRM)
  R1: 6, // Business Owner
  R2: 5, // Business Admin
  R3: 4, // Professional/Staff
  R4: 3, // Freelancer
  R7: 2, // Booth Renter
  R5: 1, // Customer
};

/**
 * Determine primary role from roles array based on privilege ranking
 * @param {string[]} roles - Array of role codes
 * @returns {string} - Primary role code
 */
function determinePrimaryRole(roles) {
  if (!roles || roles.length === 0) return "R5"; // Default to customer
  
  // Sort by priority and return highest
  const sorted = roles.sort((a, b) => (ROLE_PRIORITY[b] || 0) - (ROLE_PRIORITY[a] || 0));
  return sorted[0];
}

/**
 * Fetch user profile data to supplement JWT payload
 * @param {string} token - JWT token
 * @returns {Promise<Object|null>} - User profile or null
 */
async function fetchUserProfile(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) return null;
    const data = await response.json();
    return data.user || null;
  } catch (error) {
    return null;
  }
}

/**
 * Identify logged-in user to the CRM widget
 * Automatically called when user logs in or token is refreshed
 */
export async function identifyUserToCRM() {
  try {
    // Check if widget is loaded
    if (!window.OgestyleCRM || typeof window.OgestyleCRM.identify !== 'function') {
      return;
    }

    const token = localStorage.getItem('ogestyle_token');
    if (!token) {
      return;
    }

    // Decode JWT to get basic user data
    const payload = decodeJwtPayload(token);
    if (!payload || !payload.userId) {
      return;
    }

    // Fetch full user profile for additional data
    const userProfile = await fetchUserProfile(token);
    if (!userProfile) {
    }

    // Determine primary role from JWT roles array
    const roles = payload.roles || [payload.role || 'R5'];
    const primaryRole = determinePrimaryRole(roles);

    // Build identity payload according to CRM spec
    const identityData = {
      userId: payload.userId,
      firstName: userProfile?.firstName || userProfile?.name?.split(' ')[0] || 'User',
      lastName: userProfile?.lastName || userProfile?.name?.split(' ').slice(1).join(' ') || '',
      email: userProfile?.email || payload.email || '',
      primaryRole: primaryRole,
      businessName: userProfile?.business?.name || userProfile?.businessName || null,
      businessSlug: userProfile?.business?.slug || userProfile?.businessSlug || null,
      profilePicture: userProfile?.avatarUrl || userProfile?.profilePicture || null,
      phone: userProfile?.phone || null,
      subscriptionStatus: userProfile?.subscriptionStatus || null,
      plan: userProfile?.plan || null,
    };

    // Log for debugging (remove sensitive data in production)
    // Call the CRM widget identify method
    window.OgestyleCRM.identify(identityData);
  } catch (error) {
  }
}

/**
 * Reset/clear CRM widget session
 * Call this when user logs out
 */
export function resetCRMWidget() {
  try {
    if (!window.OgestyleCRM || typeof window.OgestyleCRM.reset !== 'function') {
      return;
    }

    window.OgestyleCRM.reset();
  } catch (error) {
  }
}

/**
 * Check if CRM widget is loaded and ready
 * @returns {boolean}
 */
export function isCRMWidgetReady() {
  return (
    window.OgestyleCRM &&
    typeof window.OgestyleCRM.identify === 'function' &&
    typeof window.OgestyleCRM.reset === 'function'
  );
}
